/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Checkbox, CheckboxGroup, LoadingItem, Skeleton, StandaloneCheckbox, Typography2 } from '@coursera/cds-core';

import CoachSkipLink from 'bundles/ai-coach-platform/components/patterns/chat/common/CoachSkipLink';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import type { CoachMemory } from 'bundles/ai-coach-platform/components/patterns/chat/settings/types';
import { getFeatureNameLabels } from 'bundles/ai-coach-platform/utils/brandLabels';

import _t from 'i18n!nls/ai-coach-platform';

type Props = {
  isDeleting: boolean;
  isAllMemorySelected: boolean;
  totalMemoryCount: number;
  isDeleteButtonDisabled: boolean;
  isLoadingMoreItems: boolean;
  selectedMemories: string[];
  memories: CoachMemory[];
  setSelectedMemories: (memoryIds: string[]) => void;
  setIsAllMemorySelected: (isAllMemorySelected: boolean) => void;
  onFetchMoreMemories: () => void;
  deleteButtonRef: React.RefObject<HTMLButtonElement>;
  memoryEnabled: boolean;
};

const styles = {
  memoryList: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    margin-top: var(--cds-spacing-150);
    container-type: inline-size;
    container-name: memory-list-container;
  `,
  memoryContentSection: css`
    /* CDS overrides */
    --cds-color-interactive-primary: var(--cds-color-blue-700);

    background-color: var(--cds-color-emphasis-neutral-background-xweak);
    padding: var(--cds-spacing-50) var(--cds-spacing-100);
    border-radius: var(--cds-border-radius-100);

    label.cds-checkboxAndRadio-label {
      margin-bottom: 0 !important;
    }

    .cds-checkboxAndRadio-disabled .cds-checkboxAndRadio-label {
      cursor: not-allowed !important;
    }
  `,
  emptyState: css`
    height: 40vh;
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: var(--cds-border-radius-100);
    padding: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-150);
  `,
  skeletonSection: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
    overflow: hidden;
  `,
  memoryCheckboxSection: css`
    overflow-y: scroll;
    height: 40vh;

    /* use more vertical space when available */
    @container memory-list-container (min-width: 400px) {
      height: 44vh;
    }
  `,
  memoryItem: css`
    .cds-checkboxAndRadio-labelContent {
      -webkit-box-orient: vertical;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }
  `,
  selectAllMemorySection: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `,
  skipLink: css`
    padding: 0 var(--cds-spacing-50);
  `,
  infiniteScrollTarget: css`
    height: 10px;
    width: 20vh;
  `,
};

const MemorySkeleton = () => <Skeleton height={40} variant="rect" />;
const MemorySkeletonRows = () => {
  return (
    <div css={styles.skeletonSection} data-testid="memory-skeleton-rows">
      <MemorySkeleton />
      <MemorySkeleton />
      <MemorySkeleton />
      <MemorySkeleton />
      <MemorySkeleton />
      <MemorySkeleton />
      <MemorySkeleton />
    </div>
  );
};
const MemoryLoader = ({ count = 0 }) => {
  return count === 0 ? <LoadingItem label={_t('Loading items')} /> : <LoadingItem label={_t('Loading more items')} />;
};
const MemoryEmptyState = ({ enabled = false, isBrandUpdateEnabled = false }) => {
  const { featureNameLabel } = getFeatureNameLabels(isBrandUpdateEnabled, 'Coach');
  return (
    <div css={styles.emptyState}>
      <Typography2 variant="bodySecondary" color="supportText" component="div">
        {enabled
          ? _t('Your details and preferences from past chats will be displayed here.')
          : _t("You've opted out of memory. #{featureNameLabel} won't create or use memories.", { featureNameLabel })}
      </Typography2>
    </div>
  );
};

export const MemoryList = (props: Props): React.ReactElement => {
  const {
    isAllMemorySelected,
    totalMemoryCount,
    isDeleteButtonDisabled,
    isDeleting,
    isLoadingMoreItems,
    selectedMemories,
    memories,
    setSelectedMemories,
    setIsAllMemorySelected,
    onFetchMoreMemories,
    deleteButtonRef,
    memoryEnabled,
  } = props;
  const { isBrandUpdateEnabled } = useCoachChatContext();

  // setup IntersectionObserver for infinite scroll behavior
  const observer = React.useMemo(() => {
    return new IntersectionObserver(
      (entries) => {
        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        if (entries[0].isIntersecting) {
          onFetchMoreMemories();
        }
      },
      { threshold: 1 }
    );
  }, []); // eslint-disable-line

  React.useEffect(() => {
    return () => observer.disconnect();
  }, [observer]);

  const setInfiniteScrollRef = React.useCallback<(node: HTMLDivElement | null) => void>(
    (node) => {
      if (node) {
        observer.observe(node);
      }
    },
    [observer]
  );

  const handleCheckboxChange = (selectedMemoryId: string, checked: boolean) => {
    if (checked) {
      setSelectedMemories([...selectedMemories, selectedMemoryId]);
    } else {
      setSelectedMemories(selectedMemories.filter((memoryId) => memoryId !== selectedMemoryId));
    }

    setIsAllMemorySelected(false);
  };

  const handleSelectAllMemory = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked) {
      setIsAllMemorySelected(true);
      setSelectedMemories(memories.map((mem) => mem.id));
    } else {
      setIsAllMemorySelected(false);
      setSelectedMemories([]);
    }
  };

  const totalMemoryCountShown = memories.length;

  if (totalMemoryCount === 0 && !isLoadingMoreItems && !isDeleting) {
    return <MemoryEmptyState enabled={memoryEnabled} isBrandUpdateEnabled={isBrandUpdateEnabled} />;
  }

  return (
    <div className="coach-memories-list">
      <div css={styles.memoryList}>
        {totalMemoryCount > 0 ? (
          <div css={[styles.memoryContentSection, styles.selectAllMemorySection]}>
            {isDeleting && <MemorySkeleton />}
            {!isDeleting && (
              <>
                <StandaloneCheckbox
                  aria-label={_t('Select all memory')}
                  checked={isAllMemorySelected}
                  label={_t('Select all (#{totalMemoryCountShown} displayed out of #{totalMemoryCount})', {
                    totalMemoryCountShown,
                    totalMemoryCount,
                  })}
                  onChange={handleSelectAllMemory}
                />

                {!isDeleteButtonDisabled && (
                  <CoachSkipLink
                    targetRef={deleteButtonRef}
                    label={_t('Skip to delete')}
                    visibleCss={styles.skipLink}
                  />
                )}
              </>
            )}
          </div>
        ) : null}

        <div css={[styles.memoryContentSection, styles.memoryCheckboxSection]}>
          {isDeleting && <MemorySkeletonRows />}
          {!isDeleting && (
            <CheckboxGroup
              aria-label={_t('Memories')}
              label=""
              value={selectedMemories}
              onChange={(e, checked) => handleCheckboxChange(e.target.value, checked)}
            >
              {totalMemoryCountShown > 0 &&
                memories.map((memory, index) => {
                  const isLastMemory = index === totalMemoryCountShown - 1;
                  return (
                    <>
                      <Checkbox key={memory.id} label={memory.text} value={memory.id} css={styles.memoryItem} />
                      {/* this target handles triggering fetch for when the user scrolls to the last memory */}
                      {isLastMemory ? (
                        <div title="1st" css={styles.infiniteScrollTarget} ref={setInfiniteScrollRef} />
                      ) : null}
                    </>
                  );
                })}
            </CheckboxGroup>
          )}

          {isLoadingMoreItems ? <MemoryLoader count={totalMemoryCount} /> : null}
        </div>
      </div>
    </div>
  );
};

export default MemoryList;
